import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { BRAND, DISCLAIMER } from "@/data/content";

const SECTIONS = [
  {
    h: "Overview",
    p: "This Privacy Notice explains how Hussar Marine Concierge (HMC), a division of Hussar Holdings LLC, collects, uses, and protects information submitted through HussarMarineConcierge.com. By using this website or submitting a form, you agree to the practices described here.",
  },
  {
    h: "Information We Collect",
    p: "We collect the information you voluntarily provide through our inspection request and contact forms — such as your name, email, phone number, vessel details, and marina or dock location. We may also collect basic, non-identifying analytics about how visitors use the site.",
  },
  {
    h: "How We Use Information",
    p: "We use your information solely to respond to your inquiry, prepare a proposed scope of service, schedule dockside oversight, and communicate with you about your vessel. We do not sell your personal information.",
  },
  {
    h: "Data Protection & Isolation",
    p: "Client information is stored securely with role-based administrative access and audit controls. Future customer portal data will be isolated per account so that one client cannot access another client's records.",
  },
  {
    h: "Third-Party Coordination",
    p: "When you ask us to coordinate services, we may share only the details necessary with the licensed, independent marine contractors you approve. HMC does not perform mechanical work, certification, or vessel operation.",
  },
  {
    h: "Payments",
    p: "HMC accepts recurring monthly subscription payments through Stripe, our payment processor. Card details are entered on Stripe's secure checkout page and are handled by Stripe; HMC does not receive or store your full card number or security code. We keep only the subscription and payment status Stripe reports to us, such as your plan and whether a payment succeeded.",
  },
  {
    h: "Corporate Structure & Operating Entity",
    p: "Hussar Marine Concierge is currently operated by Hussar Holdings LLC, a Florida limited liability company. Hussar Holdings LLC intends to form a dedicated operating entity, Hussar Marine Concierge LLC, to be wholly owned (100%) by Hussar Holdings LLC. That operating entity has not yet been formed; any reference to it on this site describes an intended future structure and does not represent an existing company. This notice will be updated once the entity is formed.",
  },
  {
    h: "Service Limitations & Disclaimers",
    p: DISCLAIMER,
  },
  {
    h: "Contact",
    p: `For any privacy question or request, contact us at ${BRAND.email}.`,
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy & Terms — Hussar Marine Concierge"
        description="Privacy notice and service terms for Hussar Marine Concierge, a division of Hussar Holdings LLC."
      />
      <PageHero eyebrow="Legal" title="Privacy & Terms" subtitle="How we handle your information and the terms that govern our services." />

      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <p className="text-sm text-slate-500 dark:text-slate-400">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long" })}</p>
          <div className="mt-10 space-y-10">
            {SECTIONS.map((s) => (
              <div key={s.h}>
                <h2 className="font-serif text-2xl text-navy-900 dark:text-white">{s.h}</h2>
                <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
          <div className="gold-rule my-12" />
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Questions? <Link to="/contact" className="text-navy-900 dark:text-gold-400 font-semibold">Contact us</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
